import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback
} from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  InputAdornment,
  Tooltip,
  Button,
  FormControlLabel,
  Switch,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import { GlobalContext } from '../../utils/ContextProviders/GlobalContextProvider';
import Layout from '../../Component/layout';
import LeadsTable, { PAGE_SIZE } from './LeadsTable';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { queryLeads } from '../../Redux/util/Apis/leads';
import { getCommunities } from '../../Redux/util/Apis/communities';
import CreateLeadModal from './CreateLeadModal';

const getDefaultDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().split('T')[0],
    to: to.toISOString().split('T')[0]
  };
};

const LeadsPage = props => {
  const { userData } = useContext(GlobalContext);
  const clients = useMemo(
    () =>
      (userData?.clients || [])
        .slice()
        .sort((a, b) => a.name?.localeCompare(b.name)),
    [userData?.clients]
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [selectedCommunityIds, setSelectedCommunityIds] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [kelseyOnly, setKelseyOnly] = useState(false);
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [unarchivedOnly, setUnarchivedOnly] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const [data, setData] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const [communityOptions, setCommunityOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);

  // Effective client IDs: use all clients when none explicitly selected
  const effectiveClientIds = useMemo(
    () =>
      selectedClientIds.length > 0
        ? selectedClientIds
        : clients.map(c => c._id),
    [selectedClientIds, clients]
  );

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page on filter change
  useEffect(() => {
    setPage(0);
  }, [
    debouncedSearchTerm,
    selectedClientIds,
    selectedCommunityIds,
    selectedSources,
    selectedStages,
    kelseyOnly,
    dateRange,
    unarchivedOnly
  ]);

  // Client name lookup
  const clientMap = useMemo(() => {
    const map = {};
    clients.forEach(c => {
      map[c._id] = c.name;
    });
    return map;
  }, [clients]);

  const buildFilters = useCallback(() => {
    const filters = [
      { field: 'clientMongoId', op: 'in', operand: effectiveClientIds },
      {
        field: 'createdAt',
        op: '>=',
        operand: `${dateRange.from}T00:00:00.000Z`
      },
      { field: 'createdAt', op: '<=', operand: `${dateRange.to}T23:59:59.999Z` }
    ];

    if (unarchivedOnly) {
      filters.push({ field: 'is_archived', op: '=', operand: false });
    }
    if (selectedSources.length > 0) {
      filters.push({ field: 'source', op: 'in', operand: selectedSources });
    }
    if (selectedStages.length > 0) {
      filters.push({ field: 'stage', op: 'in', operand: selectedStages });
    }
    if (selectedCommunityIds.length > 0) {
      filters.push({
        field: 'communityMongoId',
        op: 'in',
        operand: selectedCommunityIds
      });
    }
    if (kelseyOnly) {
      filters.push({
        field: 'leasingAgentName',
        op: '=',
        operand: 'kelsey'
      });
    }
    if (debouncedSearchTerm) {
      const digits = debouncedSearchTerm.replace(/\D/g, '');
      const looksLikePhone =
        digits.length >= 4 &&
        digits.length === debouncedSearchTerm.replace(/[\s()-+.]/g, '').length;
      if (looksLikePhone) {
        filters.push({
          field: 'phone',
          op: 'ilike',
          operand: `%${digits}%`
        });
      } else {
        filters.push({
          field: 'name',
          op: 'ilike',
          operand: `%${debouncedSearchTerm}%`
        });
      }
    }
    return filters;
  }, [
    effectiveClientIds,
    dateRange,
    unarchivedOnly,
    selectedSources,
    selectedStages,
    selectedCommunityIds,
    kelseyOnly,
    debouncedSearchTerm
  ]);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    if (effectiveClientIds.length === 0) {
      setData([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    const filters = buildFilters();

    try {
      const res = await queryLeads({
        filter: { $and: filters },
        sortBy:
          sortField === 'lastRespondedAt'
            ? 'lastMessageExchangedAt'
            : sortField,
        sortOrder: sortDirection,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE
      });

      if (res?.error) {
        console.error('Failed to fetch leads:', res.message);
        setData([]);
        setTotalCount(0);
        return;
      }

      const leads = (res?.data || []).map(lead => ({
        id: lead.id,
        mongoId: lead._id,
        name: lead.name || '',
        client: lead.tenant?.name || clientMap[lead.tenantId] || '',
        community: lead.apartment?.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        leadId: lead._id || lead.id || '',
        source: lead.source || '',
        stage: lead.stage || '',
        owner:
          lead.leasingAgent?.name === 'kelsey'
            ? 'Kelsey'
            : lead.leasingAgent?.name || '',
        lostReason: lead.closedReason || '',
        lastRespondedAt: lead.lastMessageExchanged || '',
        createdAt: lead.createdAt || ''
      }));

      setData(leads);
      setTotalCount(res?.count || 0);

      // Derive filter options from results (merge with existing)
      const newSources = new Set(sourceOptions);
      const newStages = new Set(stageOptions);
      leads.forEach(l => {
        if (l.source) newSources.add(l.source);
        if (l.stage) newStages.add(l.stage);
      });
      setSourceOptions([...newSources].sort());
      setStageOptions([...newStages].sort());
    } catch (err) {
      console.error('Failed to fetch leads:', err);
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setLastRefreshedAt(new Date());
    }
  }, [
    buildFilters,
    effectiveClientIds,
    sortField,
    sortDirection,
    page,
    clientMap
  ]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Fetch communities and reset filters when clients change
  useEffect(() => {
    setSourceOptions([]);
    setStageOptions([]);
    setSelectedCommunityIds([]);
    setSelectedSources([]);
    setSelectedStages([]);
    setKelseyOnly(false);

    if (selectedClientIds.length === 0) {
      setCommunityOptions([]);
      return;
    }

    Promise.all(selectedClientIds.map(id => getCommunities(id)))
      .then(results => {
        const allCommunities = results
          .flatMap(res => {
            if (res?.error) return [];
            const data = Array.isArray(res) ? res : res?.data || [];
            return data;
          })
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCommunityOptions(allCommunities);
      })
      .catch(() => setCommunityOptions([]));
  }, [selectedClientIds]);

  const handleSortChange = field => {
    if (field === sortField) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(0);
  };

  const [csvLoading, setCsvLoading] = useState(false);

  const handleDownloadCsv = useCallback(async () => {
    if (effectiveClientIds.length === 0) return;

    setCsvLoading(true);
    const filters = buildFilters();
    const batchSize = 1000;
    let allLeads = [];
    let offset = 0;
    let hasMore = true;

    try {
      while (hasMore) {
        const res = await queryLeads({
          filter: { $and: filters },
          sortBy:
            sortField === 'lastRespondedAt'
              ? 'lastMessageExchangedAt'
              : sortField,
          sortOrder: sortDirection,
          limit: batchSize,
          offset
        });

        if (res?.error || !res?.data?.length) break;

        const leads = res.data.map(lead => ({
          name: lead.name || '',
          client: lead.tenant?.name || clientMap[lead.tenantId] || '',
          community: lead.apartment?.name || '',
          email: lead.email || '',
          phone: lead.phone || '',
          leadId: lead._id || lead.id || '',
          source: lead.source || '',
          stage: lead.stage || '',
          lastRespondedAt: lead.lastMessageExchanged || '',
          owner:
            lead.leasingAgent?.name === 'kelsey'
              ? 'Kelsey'
              : lead.leasingAgent?.name || '',
          lostReason: lead.closedReason || '',
          createdAt: lead.createdAt || ''
        }));

        allLeads = allLeads.concat(leads);
        offset += batchSize;
        hasMore = res.data.length === batchSize;
      }

      if (allLeads.length === 0) return;

      const headers = [
        'Name',
        'Client',
        'Community',
        'Email',
        'Phone',
        'Lead ID',
        'Source',
        'Stage',
        'Last Responded',
        'Owner',
        'Lost Reason',
        'Created'
      ];
      const keys = [
        'name',
        'client',
        'community',
        'email',
        'phone',
        'leadId',
        'source',
        'stage',
        'lastRespondedAt',
        'owner',
        'lostReason',
        'createdAt'
      ];
      const escapeCsv = val => {
        const str = String(val ?? '');
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      };
      const rows = allLeads.map(row =>
        keys.map(k => escapeCsv(row[k])).join(',')
      );
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV download failed:', err);
    } finally {
      setCsvLoading(false);
    }
  }, [buildFilters, effectiveClientIds, sortField, sortDirection, clientMap]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedClientIds([]);
    setSelectedCommunityIds([]);
    setSelectedSources([]);
    setSelectedStages([]);
    setKelseyOnly(false);
    setDateRange(getDefaultDateRange());
    setPage(0);
  };

  return (
    <Layout history={props.history}>
      <Box
        sx={{
          p: 2,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            my: 1
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {unarchivedOnly ? 'Unarchived leads' : 'All leads'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={
                csvLoading ? <CircularProgress size={18} /> : <DownloadIcon />
              }
              onClick={handleDownloadCsv}
              disabled={csvLoading || totalCount === 0}
              sx={{ backgroundColor: 'background.paper' }}
            >
              {csvLoading ? 'Downloading...' : 'Download CSV'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateModalOpen(true)}
            >
              New Lead
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}
        >
          {/* Search */}
          <TextField
            placeholder="Search by name or phone..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: 280,
              minWidth: 280,
              flex: '0 0 280px',
              backgroundColor: 'background.paper',
              borderRadius: '4px'
            }}
          />
          {/* Client filter */}
          <FormControl
            size="small"
            sx={{
              width: 240,
              minWidth: 240,
              backgroundColor: 'background.paper'
            }}
          >
            <InputLabel>Clients</InputLabel>
            <Select
              multiple
              value={selectedClientIds}
              onChange={e => setSelectedClientIds(e.target.value)}
              label="Clients"
              renderValue={selected =>
                selected.length === 0
                  ? ''
                  : selected.map(id => clientMap[id] || id).join(', ')
              }
            >
              {clients.map(c => (
                <MenuItem key={c._id} value={c._id}>
                  <Checkbox checked={selectedClientIds.includes(c._id)} />
                  <ListItemText primary={c.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Community filter */}
          <Tooltip
            title={
              selectedClientIds.length === 0
                ? 'Select at least one client first'
                : ''
            }
          >
            <span>
              <FormControl
                size="small"
                sx={{
                  width: 240,
                  minWidth: 240,
                  backgroundColor: 'background.paper'
                }}
                disabled={
                  selectedClientIds.length === 0 ||
                  communityOptions.length === 0
                }
              >
                <InputLabel>Community</InputLabel>
                <Select
                  multiple
                  value={selectedCommunityIds}
                  onChange={e => setSelectedCommunityIds(e.target.value)}
                  label="Community"
                  renderValue={selected => {
                    if (selected.length === 0) return '';
                    return `${selected.length} selected`;
                  }}
                >
                  {communityOptions.map(c => (
                    <MenuItem key={c.id} value={c.id}>
                      <Checkbox checked={selectedCommunityIds.includes(c.id)} />
                      <ListItemText primary={c.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </span>
          </Tooltip>

          {/* Source filter */}
          {sourceOptions.length > 0 && (
            <FormControl
              size="small"
              sx={{
                width: 180,
                minWidth: 180,
                backgroundColor: 'background.paper'
              }}
            >
              <InputLabel>Source</InputLabel>
              <Select
                multiple
                value={selectedSources}
                onChange={e => setSelectedSources(e.target.value)}
                label="Source"
                renderValue={selected =>
                  selected.length === 0 ? '' : `${selected.length} selected`
                }
              >
                {sourceOptions.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    <Checkbox checked={selectedSources.includes(opt)} />
                    <ListItemText primary={opt} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* Stage filter */}
          {stageOptions.length > 0 && (
            <FormControl
              size="small"
              sx={{
                width: 180,
                minWidth: 180,
                backgroundColor: 'background.paper'
              }}
            >
              <InputLabel>Stage</InputLabel>
              <Select
                multiple
                value={selectedStages}
                onChange={e => setSelectedStages(e.target.value)}
                label="Stage"
                renderValue={selected =>
                  selected.length === 0 ? '' : `${selected.length} selected`
                }
              >
                {stageOptions.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    <Checkbox checked={selectedStages.includes(opt)} />
                    <ListItemText primary={opt} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* Date range */}
          <TextField
            type="date"
            label="From"
            value={dateRange.from}
            onChange={e =>
              setDateRange(prev => ({ ...prev, from: e.target.value }))
            }
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160, backgroundColor: 'background.paper' }}
          />
          <TextField
            type="date"
            label="To"
            value={dateRange.to}
            onChange={e =>
              setDateRange(prev => ({ ...prev, to: e.target.value }))
            }
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160, backgroundColor: 'background.paper' }}
          />
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            sx={{
              height: 40,
              whiteSpace: 'nowrap',
              backgroundColor: 'background.paper'
            }}
          >
            Clear Filters
          </Button>
          <Button
            variant="outlined"
            onClick={fetchLeads}
            sx={{
              height: 40,
              minWidth: 40,
              px: 1,
              backgroundColor: 'background.paper'
            }}
          >
            <RefreshIcon />
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={kelseyOnly}
                onChange={e => setKelseyOnly(e.target.checked)}
              />
            }
            label="Kelsey only"
          />
          <FormControlLabel
            control={
              <Switch
                checked={unarchivedOnly}
                onChange={e => setUnarchivedOnly(e.target.checked)}
              />
            }
            label={unarchivedOnly ? 'Unarchived only' : 'All leads'}
          />
        </Box>

        {lastRefreshedAt && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Typography variant="caption" color="text.secondary">
              Refreshed at {lastRefreshedAt.toLocaleTimeString()}
            </Typography>
          </Box>
        )}

        <LeadsTable
          data={data}
          totalCount={totalCount}
          page={page}
          onPageChange={setPage}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          loading={loading}
        />
      </Box>

      <CreateLeadModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          setCreateModalOpen(false);
          fetchLeads();
        }}
      />
    </Layout>
  );
};

export default LeadsPage;
